import { Link } from "wouter";
import { Play, ArrowRight, Boxes, Loader2, X } from "lucide-react";
import { useVoidGame } from "@/lib/queries";
import type { Game } from "@/lib/types";

// Resume-game banner. Shows EVERY game where the signed-in user is
// on the roster and the game is still in progress (status="live"
// or "scheduled"). Games persist across app close / sign-out
// indefinitely — they only leave this list when one team hits the
// target score OR a player explicitly cancels via the × button.
// That's the foundation of the async multiplayer flow: you can
// have multiple games running concurrently with different
// opponents and step in/out of each one when you have a moment.
//
// Recovery scenarios this covers:
//   1. You backed out of a virtual game mid-play and want a
//      one-tap return.
//   2. Someone ELSE created a 1v1 game with you on the team —
//      the push notification might have been missed or dismissed;
//      this banner makes sure the invite doesn't get lost.
//   3. You have multiple in-flight games (e.g. one vs Sophia, one
//      vs Shelleen) and want a list of all of them in one place.
//
// Each row RESUMES straight into the right playing surface (not the
// read-only detail page — that had no start/resume affordance for a
// game still "scheduled"):
//   - virtual game → /dashboard/virtual/play/:shareCode (3D renderer)
//   - live game    → /score/:shareCode (the scorer; scoring the first
//     frame auto-marks a scheduled game live)

type Props = {
  games: Game[];
  myPlayerId: string | null;
};

export function ActiveGamesBanner({ games, myPlayerId }: Props) {
  // useVoidGame must live above any conditional return so the hook
  // call order stays stable across renders (banner mounts when the
  // user has zero games then later games > 0).
  const voidGame = useVoidGame();

  if (games.length === 0 || !myPlayerId) return null;

  return (
    <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2">
      <div className="flex items-center gap-2 text-xs font-display uppercase tracking-widest text-primary">
        <Loader2 size={12} className="animate-spin" />
        {games.length === 1 ? "Game in progress" : `${games.length} games in progress`}
      </div>
      <ul className="space-y-1.5">
        {games.map((g) => {
          // From this user's perspective, who's the opponent?
          // For singles: the only player on the other team. For
          // doubles: the two players on the other team joined
          // with " & ". Falls back to a generic label if
          // somehow neither team contains the user (shouldn't
          // happen — we filtered by playerId — but defensive).
          const onTeam1 = g.team1Players.some((p) => p.id === myPlayerId);
          const onTeam2 = g.team2Players.some((p) => p.id === myPlayerId);
          const oppTeam = onTeam1
            ? g.team2Players
            : onTeam2
              ? g.team1Players
              : [];
          const oppLabel =
            oppTeam.map((p) => p.name).join(" & ") || "opponent";
          const href = g.isVirtual
            ? `/dashboard/virtual/play/${g.shareCode}`
            : `/score/${g.shareCode}`;
          return (
            <li
              key={g.id}
              className="flex items-stretch gap-1 p-1.5 rounded-md bg-background border border-border hover:bg-muted/30 transition-colors"
            >
              {/* Main clickable area — Resume. Span the row except
                  for the cancel × button on the right. */}
              <Link
                href={href}
                className="flex-1 flex items-center gap-3 px-2 py-1 rounded-sm min-w-0"
              >
                {/* Mode badge — visual cue for which surface this
                    will deep-link into. */}
                <span
                  className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded ${
                    g.isVirtual
                      ? "bg-primary/15 text-primary"
                      : "bg-muted text-muted-foreground"
                  }`}
                  aria-hidden
                >
                  {g.isVirtual ? <Boxes size={14} /> : <Play size={14} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    vs {oppLabel}
                  </div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">
                    {g.isVirtual ? "Virtual" : "Live"} · {g.team1Score}–{g.team2Score}
                    {g.status === "scheduled" ? " · waiting to start" : ""}
                  </div>
                </div>
                <span className="text-xs text-primary font-display uppercase tracking-widest inline-flex items-center gap-1 shrink-0">
                  Resume <ArrowRight size={12} />
                </span>
              </Link>
              {/* Cancel game — separate clickable element so the
                  whole row isn't a confirm trap. Voids the game
                  (status → voided) on confirm; React Query
                  invalidation makes it disappear from the banner
                  immediately. Game stays in history as "voided"
                  rather than vanishing entirely, so you can still
                  see it in past-games lists. */}
              <button
                type="button"
                onClick={() => {
                  const ok = confirm(
                    `Cancel game vs ${oppLabel}? It'll be marked as voided.`,
                  );
                  if (!ok) return;
                  voidGame.mutate(g.id);
                }}
                disabled={voidGame.isPending}
                className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-50"
                aria-label={`Cancel game vs ${oppLabel}`}
                title={`Cancel game vs ${oppLabel}`}
              >
                <X size={14} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
